import { useCallback, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Search, ShieldAlert, Download, Trash2 } from "lucide-react";

export default function AdminGdpr() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");

  const search = useCallback(async () => {
    if (!q.trim()) return;
    setLoading(true);
    try { const { data } = await api.get("/admin/users", { params: { q: q.trim() } }); setRows(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Search failed."); }
    finally { setLoading(false); }
  }, [q]);

  const exportData = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/users/${sel.user_id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jobsmith-user-${sel.email || sel.user_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exported.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Export failed."); }
    finally { setBusy(false); }
  };

  const deleteUser = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${sel.user_id}`);
      const n = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Deleted ${sel.email} and ${n} record(s).`);
      setSel(null);
      setConfirmText("");
      setRows((rs) => rs.filter((r) => r.user_id !== sel.user_id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Delete failed."); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="admin-gdpr">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><ShieldAlert className="h-7 w-7 text-[#24468C]" strokeWidth={1.75} /> GDPR / Data Requests</h1>
        <p className="text-muted-foreground text-sm">Export or permanently delete a user's data on request. All actions are audit-logged.</p>
      </div>

      <div className="flex gap-2 max-w-md">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input className="pl-9" placeholder="Search by email or user ID" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="gdpr-search-input" />
        </div>
        <Button onClick={search} disabled={loading} data-testid="gdpr-search-btn">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Search"}</Button>
      </div>

      {rows.length > 0 && (
        <div className="space-y-2" data-testid="gdpr-results">
          {rows.map((u) => (
            <Card key={u.user_id} className={`border-zinc-200 cursor-pointer transition-colors ${sel?.user_id === u.user_id ? "ring-2 ring-[#24468C]" : "hover:bg-zinc-50"}`} onClick={() => { setSel(u); setConfirmText(""); }} data-testid={`gdpr-user-${u.user_id}`}>
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="font-medium">{u.email}</p>
                  <p className="text-xs text-muted-foreground">{u.name || "—"} · joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</p>
                </div>
                {sel?.user_id === u.user_id && <span className="text-xs font-semibold text-[#24468C]">Selected</span>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {sel && (
        <Card className="border-zinc-200" data-testid="gdpr-actions">
          <CardContent className="p-5 space-y-4">
            <p className="font-semibold">{sel.email}</p>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={exportData} disabled={busy} data-testid="gdpr-export-btn"><Download className="h-4 w-4 mr-1.5" /> Export data (JSON)</Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" disabled={busy} data-testid="gdpr-delete-btn"><Trash2 className="h-4 w-4 mr-1.5" /> Delete all data</Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Permanently delete {sel.email}?</AlertDialogTitle>
                    <AlertDialogDescription>This erases the account and ALL associated data (profile, CVs, applications, subscriptions, leads, referrals). This cannot be undone. Type <strong>DELETE</strong> to confirm.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <Input value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type DELETE" data-testid="gdpr-confirm-input" />
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="gdpr-cancel">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={deleteUser} disabled={confirmText !== "DELETE" || busy} className="bg-red-600 hover:bg-red-700" data-testid="gdpr-confirm-delete">Delete everything</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
